// @spec SUP-STR-008
'use client';

import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Settings2 } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RiskScoringConfigForm,
  useRecalculateRiskScores,
  useResetRiskScoringConfig,
  useRiskScoringConfig,
  useUpdateRiskScoringConfig,
} from '@/features/suppliers/admin/risk-config';
import type { RiskScoringConfigFormData } from '@/features/suppliers/admin/risk-config';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/suppliers/admin/risk-config')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.riskScoringConfiguration'),
  }),
  component: RiskConfigPage,
});

/**
 * Risk Scoring Configuration page for organization admins.
 *
 * Allows management of risk factors and their weights used to calculate
 * supplier risk scores.
 */
function RiskConfigPage() {
  const { t } = useTranslation(['suppliers', 'common']);
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);

  // Data fetching
  const {
    data: config,
    isLoading: configLoading,
    error: configError,
    refetch,
  } = useRiskScoringConfig(activeOrg?.id);

  // Mutations
  const updateConfig = useUpdateRiskScoringConfig({
    onSuccess: () => {
      toast.success(t('suppliers:toast.riskScoringConfigurationSaved'));
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('suppliers:toast.failedToSaveConfiguration'));
    },
  });

  const recalculate = useRecalculateRiskScores({
    onSuccess: (data) => {
      toast.success(
        t('suppliers:toast.riskScoreRecalculationStartedForSuppliers', {
          count: data.affectedSuppliers,
        })
      );
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('suppliers:toast.failedToStartRecalculation'));
    },
  });

  const resetConfig = useResetRiskScoringConfig({
    onSuccess: () => {
      toast.success(t('suppliers:toast.configurationResetToDefaults'));
      void refetch();
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('suppliers:toast.failedToResetConfiguration'));
    },
  });

  // Handlers
  const handleSubmit = React.useCallback(
    async (data: RiskScoringConfigFormData) => {
      if (!activeOrg?.id) return;
      await updateConfig.mutateAsync({
        orgId: activeOrg.id,
        data: {
          factors: data.factors.map((f) => ({
            id: f.id ?? undefined,
            name: f.name,
            description: f.description ?? undefined,
            weight: f.weight,
            scoringCriteria: {
              type: f.scoringCriteria.type,
              options: f.scoringCriteria.options?.map((o) => ({
                value: o.value,
                label: o.label,
                score: o.score,
              })),
              minScore: f.scoringCriteria.minScore ?? undefined,
              maxScore: f.scoringCriteria.maxScore ?? undefined,
            },
            isEnabled: f.isEnabled,
            order: f.order,
            fieldMapping: f.fieldMapping ?? undefined,
          })),
        },
      });
    },
    [activeOrg?.id, updateConfig]
  );

  const handleRecalculate = React.useCallback(async () => {
    if (!activeOrg?.id) return;
    await recalculate.mutateAsync({ orgId: activeOrg.id });
  }, [activeOrg?.id, recalculate]);

  const handleReset = React.useCallback(async () => {
    if (!activeOrg?.id) return;
    await resetConfig.mutateAsync({ orgId: activeOrg.id });
  }, [activeOrg?.id, resetConfig]);

  // Loading state
  if (orgsLoading || configLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-4 w-64 rounded bg-muted" />
            <div className="h-32 rounded bg-muted" />
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No active organization
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
            <Button variant="outline" asChild className="mt-4">
              <Link to="/settings/organizations">{t('common:routeErrors.goToOrganizations')}</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (configError) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.errorLoadingConfiguration')}</AlertTitle>
            <AlertDescription>
              {configError.message || t('common:routeErrors.failedToLoadRiskScoringConfiguration')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => void refetch()} className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // No config loaded (shouldn't happen with get_or_create)
  if (!config) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.noConfigurationFound')}</AlertTitle>
            <AlertDescription>Initializing default risk scoring configuration...</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <header className="space-y-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/suppliers">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Suppliers
              </Link>
            </Button>
          </div>
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary/10 p-3">
              <Settings2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Risk Scoring Configuration</h1>
              <p className="mt-1 text-muted-foreground">
                Configure the factors and weights used to calculate supplier risk scores.
              </p>
            </div>
          </div>
        </header>

        {/* Form */}
        <RiskScoringConfigForm
          initialData={config}
          onSubmit={handleSubmit}
          onRecalculate={handleRecalculate}
          onReset={handleReset}
          isLoading={updateConfig.isPending}
          isRecalculating={recalculate.isPending}
          isResetting={resetConfig.isPending}
        />
      </div>
    </div>
  );
}
